/**
 * PultBoard — the board of the pult, part for part, and the hand on its controls (task 54).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Экран, раскладка, поворот», «Лупа и
 * увеличение». Every part of `lib/pult-layout.ts` is one absolutely placed element in points of
 * the board (`--x`, `--y`, `--w`, `--h`, `--fs`), and `styles/pult.css` turns points into shares
 * of the board, as `deck.css` does with millimetres: the board scales as one picture, sharp at any
 * spread of the fingers, and no part moves against another.
 *
 * ## Every finger is its own press
 *
 * Each control is an element with its own Pointer Events, and it keeps the `pointerId` of each
 * finger on it (`setPointerCapture`): two fingers on two pads are two presses, and a finger that
 * slides off a knob still turns it. A key acts as the finger lands — a pult's keys do — and a
 * keyboard's Enter does the same through `click` (`detail === 0`), so a tap is never two presses.
 *
 * - a key, a pad: pressed while held; held {@link PAD_HOLD_MS} — its second action, where the
 *   device had SHIFT (task 60: a HOT CUE taken away; task 62: RELOOP);
 * - a knob: the finger's travel up and down, one point of the screen a hundredth of the turn;
 * - a fader, the crossfader: the cap goes with the finger by as much as it moved, from where it
 *   stood — taking a fader never jumps it to the finger;
 * - a paddle: up, and down again;
 * - a platter: the turntable's own gesture, on its own deck (task 57, {@link Platter}).
 *
 * ## Asleep
 *
 * In the magnifier's state (`asleep`) no control takes anything: the screen lays a gesture layer
 * over the board, the stylesheet takes the controls' pointer events away, and every handler here
 * returns before it does anything — the last is what a test can see.
 *
 * What a key does is the screen's (`onKey`, `onKeyUp`, and a pad's long press `onKeyHold` —
 * `screens/Pult.tsx`). A knob or a fader moves a value of `store/pult.ts`, and a deck's tempo
 * fader that deck's tempo (task 55); tasks 56–58 give the other values their sound.
 */

import type { CSSProperties, JSX, KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import adapterFlagUrl from '../assets/adapter-flag.svg';
import labelJamaica45Url from '../assets/label-jamaica-45.svg';
import { player } from '../engine/player';
import { scratch } from '../engine/scratch';
import type { ScratchHand } from '../engine/scratch';
import { createPlatterGesture } from '../lib/platter';
import type { PlatterGesture } from '../lib/platter';
import { PAD_COLOURS } from '../lib/pult-layout';
import { PAD_HOLD_MS } from '../lib/pult-pads';
import type { DeckSide, PultBoardLayout, PultPart, PultRect, PultSide } from '../lib/pult-layout';
import { TURN_S, nominalDegPerS } from '../lib/records';
import type { RecordSize } from '../lib/records';
import { telegram } from '../lib/telegram';
import { PITCH_RANGE, leaveDeck, setPitch, takeDeck, useDeck } from '../store/deck';
import { setHeld, setValue, usePult, valueOf } from '../store/pult';

import { useReducedMotion } from './motion';

/** What the controls are called for a screen reader and in tests: the print, or what it means. */
const NAMES: Readonly<Record<string, string>> = Object.freeze({
  'jump.back': 'BEAT JUMP назад',
  'jump.fwd': 'BEAT JUMP вперёд',
  jumpsize: 'JUMP SIZE',
  slip: 'SLIP',
  censor: 'CENSOR',
  keylock: 'KEYLOCK',
  platter: 'пластинка',
  rpm33: '33',
  rpm45: '45',
  reset: 'RESET',
  pitch: 'темп',
  sync: 'SYNC',
  cue: 'CUE',
  play: 'START·STOP',
  'mode.hotcue': 'режим HOT CUE',
  'mode.roll': 'режим ROLL',
  'mode.sampler': 'режим SAMPLER',
  'mode.loop': 'режим LOOP',
  oled: 'подписи падов',
  'param.prev': 'PARAMETER назад',
  'param.next': 'PARAMETER вперёд',
  'bend.down': 'PITCH BEND −',
  'bend.up': 'PITCH BEND +',
  loop: 'LOOP ON/OFF',
  'loop.half': 'петля ½',
  'loop.double': 'петля 2×',
  pullup: 'PULL UP',
  echoout: 'ECHO OUT',
  level: 'LEVEL',
  high: 'HIGH',
  mid: 'MID',
  low: 'LOW',
  filter: 'FILTER',
  fx: 'рычаг FX',
  fader: 'фейдер',
  'load.A': 'LOAD на деку A',
  'load.B': 'LOAD на деку B',
  'screen.queue': 'экран QUEUE',
  'screen.library': 'экран LIBRARY',
  'screen.view': 'экран VIEW',
  'fx.echo': 'эффект ECHO',
  'fx.reverb': 'эффект REVERB',
  'fx.flanger': 'эффект FLANGER',
  'fx.phaser': 'эффект PHASER',
  'fx.beats': 'BEATS',
  'fx.depth': 'DEPTH',
  auto: 'AUTO',
  preset: 'PRESET',
  xfader: 'кроссфейдер',
});

/** The name of a control: «Дека A: KEYLOCK», «пад 3» on its deck, «кроссфейдер». */
function controlName(id: string, side: PultSide): string {
  if (side === 'mid') return NAMES[id] ?? id;
  const own = id.slice(2);
  const pad = /^pad(\d)$/u.exec(own);
  return `Дека ${side}: ${pad === null ? (NAMES[own] ?? own) : `пад ${pad[1] ?? ''}`}`;
}

/** What a deck shows of its own: its record, and the words over its pads, two to a cell. */
export interface PultDeckLights {
  readonly record: RecordSize;
  readonly cells: readonly (readonly [string, string])[];
}

/** What lights the board: the app's state, read by the screen and handed down. */
export interface PultLights {
  readonly decks: Readonly<Record<DeckSide, PultDeckLights>>;
  /** Ids of the keys lit now: START·STOP of a deck that plays, the effect chosen, AUTO, … */
  readonly lit: ReadonlySet<string>;
}

export interface PultBoardProps {
  readonly layout: PultBoardLayout;
  readonly asleep: boolean;
  readonly lights: PultLights;
  /** A key pressed. */
  readonly onKey: (id: string) => void;
  /** Task 55: the last finger left a key — PITCH BEND lets go. */
  readonly onKeyUp: (id: string) => void;
  /** Task 60: a finger stayed on a key or a pad {@link PAD_HOLD_MS} — its second action. */
  readonly onKeyHold: (id: string) => void;
  /** What the screen in the middle shows. */
  readonly screen: ReactNode;
  /** The window onto the board, as a CSS transform (`lib/pult-zoom.ts`). */
  readonly transform: string;
  /** Task 56: MAIN's level now, 0…1 — read by the meter while the board is drawn. */
  readonly level: () => number;
}

function at(rect: PultRect, extra: Readonly<Record<string, number | string>> = {}): CSSProperties {
  return { '--x': rect.x, '--y': rect.y, '--w': rect.w, '--h': rect.h, ...extra } as CSSProperties;
}

/** Takes the finger for the element, so it stays this control's off the edge. */
function capture(event: PointerEvent<HTMLElement>): void {
  try {
    event.currentTarget.setPointerCapture(event.pointerId);
  } catch {
    // A webview without pointer capture: the control holds the finger while it stays on it.
  }
}

/* ── keys and pads ─────────────────────────────────────────────────────────── */

/**
 * A key's or a pad's fingers. `onHold` (task 60): a finger stayed {@link PAD_HOLD_MS} — never after
 * the last finger left, nor once the board sleeps or the control is gone, whose lift may never come.
 */
function usePress(
  asleep: boolean,
  onPress: () => void,
  onRelease: () => void = () => undefined,
  onHold?: () => void,
) {
  const fingers = useRef(new Set<number>());
  const [pressed, setPressed] = useState(false);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stopHold = useCallback((): void => {
    if (hold.current !== undefined) clearTimeout(hold.current);
    hold.current = undefined;
  }, []);
  useEffect(() => stopHold, [stopHold]);
  useEffect(() => {
    if (asleep) stopHold();
  }, [asleep, stopHold]);
  const release = (event: PointerEvent<HTMLElement>): void => {
    if (!fingers.current.delete(event.pointerId)) return;
    if (fingers.current.size > 0) return;
    stopHold();
    setPressed(false);
    onRelease();
  };
  return {
    pressed,
    handlers: {
      onPointerDown: (event: PointerEvent<HTMLElement>): void => {
        if (asleep) return;
        capture(event);
        fingers.current.add(event.pointerId);
        setPressed(true);
        onPress();
        if (onHold === undefined) return;
        stopHold();
        hold.current = setTimeout(() => {
          hold.current = undefined;
          onHold();
        }, PAD_HOLD_MS);
      },
      onPointerUp: release,
      onPointerCancel: release,
      onClick: (event: MouseEvent<HTMLElement>): void => {
        // A tap has acted as it landed; this is Enter or Space on a keyboard — a press and its
        // release at once, so a key held by a finger (PITCH BEND) is not left held.
        if (asleep || event.detail !== 0) return;
        onPress();
        onRelease();
      },
    },
  };
}

const Key = memo(function Key({
  part,
  asleep,
  lit,
  onKey,
  onKeyUp,
  onKeyHold,
}: {
  readonly part: Extract<PultPart, { kind: 'key' }>;
  readonly asleep: boolean;
  readonly lit: boolean;
  readonly onKey: (id: string) => void;
  readonly onKeyUp: (id: string) => void;
  readonly onKeyHold: (id: string) => void;
}): JSX.Element {
  const { pressed, handlers } = usePress(
    asleep,
    () => {
      onKey(part.id);
    },
    () => {
      onKeyUp(part.id);
    },
    () => {
      onKeyHold(part.id);
    },
  );
  return (
    <button
      type="button"
      className={`pp ppKey look-${part.look}`}
      style={at(part.rect, { '--fs': part.fs })}
      aria-label={controlName(part.id, part.side)}
      aria-pressed={part.toggle === true ? lit : undefined}
      data-lit={lit ? 'true' : 'false'}
      data-pressed={pressed ? 'true' : 'false'}
      {...handlers}
    >
      {part.glyph === undefined ? (
        part.text
      ) : (
        <svg viewBox="0 0 10 10" aria-hidden="true" className="ppGlyph">
          <polygon points={part.glyph === 'left' ? '7,1.5 7,8.5 2,5' : '3,1.5 3,8.5 8,5'} />
        </svg>
      )}
    </button>
  );
});

const Pad = memo(function Pad({
  part,
  asleep,
  lit,
  onKey,
  onKeyUp,
  onKeyHold,
}: {
  readonly part: Extract<PultPart, { kind: 'pad' }>;
  readonly asleep: boolean;
  readonly lit: boolean;
  readonly onKey: (id: string) => void;
  readonly onKeyUp: (id: string) => void;
  readonly onKeyHold: (id: string) => void;
}): JSX.Element {
  const { pressed, handlers } = usePress(
    asleep,
    () => {
      onKey(part.id);
    },
    () => {
      onKeyUp(part.id);
    },
    () => {
      onKeyHold(part.id);
    },
  );
  const [c, hi, lo, g] = PAD_COLOURS[part.colour] ?? PAD_COLOURS[0] ?? ['', '', '', ''];
  return (
    <button
      type="button"
      className="pp ppPad"
      style={at(part.rect, { '--c': c, '--hi': hi, '--lo': lo, '--g': g })}
      aria-label={controlName(part.id, part.side)}
      data-lit={lit ? 'true' : 'false'}
      data-pressed={pressed ? 'true' : 'false'}
      {...handlers}
    />
  );
});

/* ── knobs, faders, paddles ────────────────────────────────────────────────── */

/**
 * Where a knob or a fader stands, 0…1, and how to move it: the pult's own value
 * (`store/pult.ts`), or — a deck's tempo fader, since task 55 — that deck's tempo, the top of the
 * slot «−8 %» and its foot «+8 %», as printed beside it.
 */
function useControl(id: string): readonly [number, (value: number) => void] {
  const side = id === 'A.pitch' ? 'A' : id === 'B.pitch' ? 'B' : undefined;
  const tempo = useDeck((s) => (side === undefined ? 0 : s.decks[side].pitch));
  const own = usePult((s) => valueOf(s.values, id));
  if (side === undefined) {
    return [
      own,
      (value) => {
        setValue(id, value);
      },
    ];
  }
  return [
    (tempo + PITCH_RANGE) / (2 * PITCH_RANGE),
    (value) => {
      setPitch(Math.min(1, Math.max(0, value)) * 2 * PITCH_RANGE - PITCH_RANGE, side);
    },
  ];
}

/**
 * A knob's or a fader's finger: where it came down and the value then, kept until it leaves.
 * `travel` turns the finger's way from there into the new value; `undefined` moves nothing.
 */
function useDrag(
  id: string,
  value: number,
  write: (value: number) => void,
  asleep: boolean,
  travel: (dx: number, dy: number) => number | undefined,
) {
  const drag = useRef<{ pointerId: number; from: number; x: number; y: number } | undefined>(
    undefined,
  );
  const end = (event: PointerEvent<HTMLElement>): void => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = undefined;
    // Task 56: let go — the automat's again from its next write.
    setHeld(id, false);
  };
  return {
    onPointerDown: (event: PointerEvent<HTMLElement>): void => {
      if (asleep) return;
      capture(event);
      // Task 56: under a finger — the automat writes nothing here.
      setHeld(id, true);
      drag.current = {
        pointerId: event.pointerId,
        from: value,
        x: event.clientX,
        y: event.clientY,
      };
    },
    onPointerMove: (event: PointerEvent<HTMLElement>): void => {
      const held = drag.current;
      if (asleep || held?.pointerId !== event.pointerId) return;
      const moved = travel(event.clientX - held.x, event.clientY - held.y);
      if (moved !== undefined) write(held.from + moved);
    },
    onPointerUp: end,
    onPointerCancel: end,
    // The system took the finger: it is gone as surely as lifted.
    onLostPointerCapture: end,
    // Arrow keys move it by a twentieth.
    onKeyDown: (event: KeyboardEvent<HTMLElement>): void => {
      if (asleep) return;
      const step =
        event.key === 'ArrowUp' || event.key === 'ArrowRight'
          ? 0.05
          : event.key === 'ArrowDown' || event.key === 'ArrowLeft'
            ? -0.05
            : 0;
      if (step === 0) return;
      event.preventDefault();
      write(value + step);
    },
  };
}

const Knob = memo(function Knob({
  part,
  asleep,
}: {
  readonly part: Extract<PultPart, { kind: 'knob' }>;
  readonly asleep: boolean;
}): JSX.Element {
  const [value, write] = useControl(part.id);
  // One point of the screen is a hundredth of the turn; up turns it right.
  const handlers = useDrag(part.id, value, write, asleep, (_, dy) => -dy / 100);
  // A filter's ring is lit while it filters: off the middle of its travel.
  const resting = part.id.endsWith('filter') && Math.abs(value - 0.5) < 0.02;
  return (
    <div
      className={`pp ppKnob look-${part.look}`}
      style={at(part.rect, { '--turn': `${String(Math.round((value - 0.5) * 270))}deg` })}
      role="slider"
      tabIndex={0}
      aria-label={controlName(part.id, part.side)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      data-lit={resting ? 'false' : 'true'}
      {...handlers}
    />
  );
});

const Fader = memo(function Fader({
  part,
  asleep,
}: {
  readonly part: Extract<PultPart, { kind: 'fader' }>;
  readonly asleep: boolean;
}): JSX.Element {
  const [value, write] = useControl(part.id);
  const slot = useRef<HTMLDivElement | null>(null);
  const vertical = part.axis === 'v';
  const handlers = useDrag(part.id, value, write, asleep, (dx, dy) => {
    // The slot as it is on screen now, through every scale and spread over it.
    const box = slot.current?.getBoundingClientRect();
    const length = vertical ? box?.height : box?.width;
    if (length === undefined || length <= 0) return undefined;
    return (vertical ? dy : dx) / length;
  });
  // The touch area is the cap's breadth along the whole slot, as the generator's map draws it.
  const area: PultRect = vertical
    ? {
        x: part.slot.x + part.slot.w / 2 - part.capW / 2,
        y: part.slot.y,
        w: part.capW,
        h: part.slot.h,
      }
    : {
        x: part.slot.x,
        y: part.slot.y + part.slot.h / 2 - part.capH / 2,
        w: part.slot.w,
        h: part.capH,
      };
  const local = (rect: PultRect): CSSProperties =>
    at({ x: rect.x - area.x, y: rect.y - area.y, w: rect.w, h: rect.h });
  return (
    <div
      className={`pp ppFader axis-${part.axis}`}
      style={at(area, { '--at': value, '--cap-w': part.capW, '--cap-h': part.capH })}
      role="slider"
      tabIndex={0}
      aria-label={controlName(part.id, part.side)}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      {...handlers}
    >
      {part.ticks !== undefined && <div className="pp ppTicks" style={local(part.ticks)} />}
      <div ref={slot} className="pp ppSlot" style={local(part.slot)} />
      <div className="ppCap" />
    </div>
  );
});

const Paddle = memo(function Paddle({
  part,
  asleep,
}: {
  readonly part: Extract<PultPart, { kind: 'paddle' }>;
  readonly asleep: boolean;
}): JSX.Element {
  const up = usePult((s) => valueOf(s.values, part.id) >= 0.5);
  const flip = (): void => {
    setValue(part.id, up ? 0 : 1);
  };
  return (
    <button
      type="button"
      className="pp ppPaddle"
      style={at(part.rect)}
      aria-label={controlName(part.id, part.side)}
      aria-pressed={up}
      data-lit={up ? 'true' : 'false'}
      onPointerDown={(event) => {
        if (asleep) return;
        capture(event);
        flip();
      }}
      onClick={(event) => {
        if (asleep || event.detail !== 0) return;
        flip();
      }}
    />
  );
});

/* ── the platter ───────────────────────────────────────────────────────────── */

/** A finger on a deck's platter, while it is there. */
interface PlatterGrip {
  readonly pointerId: number;
  readonly hand: ScratchHand | undefined;
  readonly gesture: PlatterGesture;
  readonly releaseSwipes: () => void;
  angle: number;
  movedAt: number;
}

/**
 * A deck's platter, and a finger on it — task 57: the turntable's gesture on its own deck
 * (`components/Vinyl.tsx`). The scratch first, by the deck's own voice (`engine/scratch.ts`); a
 * track not ready, task 09's table on the deck's speed. The hand is in the deck's own slice, one
 * finger on a platter — and any finger: the first on the glass may be on a fader, so a second one
 * is a platter's too, and two platters are two hands. Everything a touch takes is given back on
 * each way out: the lift, the cancel, the lost capture, the board unmounted or put to sleep under
 * it, «уменьшение движения» switched on.
 *
 * Under the scratch the record is the voice's picture, as on the turntable — a record drawn from
 * the finger ran ahead of the sound (`engine/scratch.ts`, «What the record is drawn from»); the
 * finger draws it only on task 09's table. The record does not turn by itself: the pult's platter
 * shows a hand, not the deck's motor.
 */
function Platter({
  part,
  size,
  asleep,
}: {
  readonly part: Extract<PultPart, { kind: 'platter' }>;
  readonly size: RecordSize;
  readonly asleep: boolean;
}): JSX.Element {
  const side = part.side === 'B' ? 'B' : 'A';
  // A phone that asked not to be moved gets no gesture, as on the turntable (`components/motion.ts`).
  const still = useReducedMotion();
  const spin = useRef<HTMLDivElement | null>(null);
  const grip = useRef<PlatterGrip | undefined>(undefined);
  const rest = useRef(0);
  const drawing = useRef<number | undefined>(undefined);

  const stopDrawing = useCallback((): void => {
    if (drawing.current !== undefined) cancelAnimationFrame(drawing.current);
    drawing.current = undefined;
  }, []);

  const letGo = useCallback((): void => {
    const current = grip.current;
    if (current === undefined) return;
    grip.current = undefined;
    // The swipes first: whatever else fails on the way out, the hold must not outlive the touch.
    current.releaseSwipes();
    current.gesture.end();
    if (current.hand === undefined) player().speedOf(side)?.release();
    else current.hand.release();
    leaveDeck('record', side);
    rest.current = current.angle;
  }, [side]);

  useEffect(
    () => () => {
      stopDrawing();
      letGo();
    },
    [letGo, stopDrawing],
  );
  useEffect(() => {
    if (asleep || still) letGo();
  }, [asleep, still, letGo]);

  /** The record turned by the deck's voice every frame: `fromAngle` where the voice was at `fromS`. */
  function drawFromVoice(fromAngle: number, fromS: number): void {
    stopDrawing();
    const turn = TURN_S[size];
    const draw = (): void => {
      const atS = scratch().voiceAt(undefined, side);
      if (atS === undefined) {
        drawing.current = undefined;
        return;
      }
      const angle = fromAngle + ((atS - fromS) / turn) * 360;
      rest.current = angle;
      if (grip.current !== undefined) grip.current.angle = angle;
      spin.current?.style.setProperty('--grab', `${String(angle)}deg`);
      drawing.current = requestAnimationFrame(draw);
    };
    draw();
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    // A finger, or the main button of a mouse; one on a platter at a time.
    if (asleep || still || grip.current !== undefined || event.button !== 0) return;
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return;
    if (!takeDeck('record', side)) return;
    // A touch during the voice's return takes the record where the voice has drawn it.
    stopDrawing();
    const hand = scratch().grab(event.timeStamp, side);
    const start = rest.current;
    const centre = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    const current: PlatterGrip = {
      pointerId: event.pointerId,
      hand,
      angle: start,
      movedAt: event.timeStamp,
      releaseSwipes: telegram.holdVerticalSwipes(),
      gesture: createPlatterGesture({
        // The deck's speed asked for each time: the active deck's may change under the finger.
        onRate: (rate) => {
          if (hand === undefined) player().speedOf(side)?.set(rate);
        },
        nominalDegPerS: nominalDegPerS(size),
        onTurn: (travel) => {
          if (hand !== undefined) {
            hand.turn(travel, current.movedAt);
            return;
          }
          current.angle = start + travel;
          spin.current?.style.setProperty('--grab', `${String(current.angle)}deg`);
        },
        now: () => performance.now(),
      }),
    };
    grip.current = current;
    capture(event);
    if (hand === undefined) player().speedOf(side)?.touch();
    else drawFromVoice(start, scratch().voiceAt(undefined, side) ?? 0);
    current.gesture.down({ x: event.clientX, y: event.clientY }, centre);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const current = grip.current;
    if (current?.pointerId !== event.pointerId) return;
    current.movedAt = event.timeStamp;
    current.gesture.move({ x: event.clientX, y: event.clientY });
  }

  function onPointerEnd(event: PointerEvent<HTMLDivElement>): void {
    if (grip.current?.pointerId === event.pointerId) letGo();
  }

  return (
    <div
      className="pp ppPlatter"
      style={at(part.rect)}
      role="img"
      aria-label={`${controlName(part.id, part.side)}, ${size}`}
      data-size={size}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => {
        onPointerEnd(event);
        // A lift is a tap to the browser: the sound of the scratch wakes here if it slept.
        scratch().prime();
      }}
      onPointerCancel={onPointerEnd}
      onLostPointerCapture={onPointerEnd}
    >
      <div className="ppDots1" />
      <div className="ppDots2" />
      <div ref={spin} className="ppSpin">
        {size === '33' ? (
          <>
            <div className="ppMat" />
            <div className="ppLabel33">
              <span>FLASH</span>
              <i />
              <span>DANCEHALL</span>
            </div>
            <div className="ppSpindle" />
          </>
        ) : (
          <>
            <div className="ppMat ppFelt" />
            <div className="ppRecord45" />
            <img className="ppLabel45" src={labelJamaica45Url} alt="" />
            <img className="ppAdapter" src={adapterFlagUrl} alt="" />
          </>
        )}
      </div>
      <div className="ppMark" />
    </div>
  );
}

/* ── what only shows ───────────────────────────────────────────────────────── */

/** The meter's segments: green, yellow, orange, red, as the generator's. */
function segmentColour(share: number): string {
  if (share < 0.6) return 'var(--pult-led-green)';
  if (share < 0.78) return 'var(--pult-led-yellow)';
  if (share < 0.9) return 'var(--pult-led-orange)';
  return 'var(--pult-led-red)';
}

/** How often MAIN's meter reads the level: ten times a second, while the pult is open. */
export const METER_EVERY_MS = 100;

/** The meter's scale: a level at this many dB below full lights nothing. */
const METER_FLOOR_DB = -48;

/** A peak level as the share of the meter it lights: in dB, from the floor to full. */
function meterShare(peak: number): number {
  if (!(peak > 0)) return 0;
  return Math.min(1, Math.max(0, (20 * Math.log10(peak) - METER_FLOOR_DB) / -METER_FLOOR_DB));
}

/**
 * MAIN's four columns (task 56): the level of the sound going to the speaker, read from the
 * meter's analyser {@link METER_EVERY_MS} — here, in the component, so it runs only while the pult
 * is drawn, and `engine/` keeps its rule of no `requestAnimationFrame` untouched.
 */
function Meter({
  part,
  level,
}: {
  readonly part: Extract<PultPart, { kind: 'meter' }>;
  readonly level: () => number;
}): JSX.Element {
  const [lit, setLit] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setLit(meterShare(level()));
    }, METER_EVERY_MS);
    return () => {
      clearInterval(timer);
    };
  }, [level]);
  const { w, h } = part.rect;
  const { segments } = part;
  const gap = w * 0.1;
  const cw = (w - gap * 3) / 4;
  const sh = h / segments;
  return (
    <svg
      className="pp ppMeter"
      style={at(part.rect)}
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      role="meter"
      aria-label="MAIN"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(lit * 100)}
    >
      {[0, 1, 2, 3].map((column) =>
        Array.from({ length: segments }, (_, s) => (
          <rect
            key={`${String(column)}-${String(s)}`}
            x={column * (cw + gap)}
            y={h - (s + 1) * sh + sh * 0.18}
            width={cw}
            height={sh * 0.64}
            rx={sh * 0.15}
            fill={segmentColour((s + 0.5) / segments)}
            opacity={(s + 0.5) / segments <= lit ? 1 : 0.13}
            data-lit={(s + 0.5) / segments <= lit ? 'true' : undefined}
          />
        )),
      )}
    </svg>
  );
}

function Oled({
  part,
  cells,
}: {
  readonly part: Extract<PultPart, { kind: 'oled' }>;
  readonly cells: readonly (readonly [string, string])[];
}): JSX.Element {
  return (
    <div
      className="pp ppOled"
      style={at(part.rect, { '--fs': part.fs })}
      aria-label={controlName(part.id, part.side)}
      role="note"
    >
      {cells.map(([top, bottom], column) => (
        // A cell is its column: two cells may read alike.
        <div key={String(column)}>
          <span>{top}</span>
          <i />
          <span>{bottom}</span>
        </div>
      ))}
    </div>
  );
}

/* ── the board ─────────────────────────────────────────────────────────────── */

export function PultBoard({
  layout,
  asleep,
  lights,
  onKey,
  onKeyUp,
  onKeyHold,
  screen,
  transform,
  level,
}: PultBoardProps): JSX.Element {
  return (
    <div
      className="pultBoard"
      style={{ transform }}
      data-orientation={layout.orientation}
      data-asleep={asleep ? 'true' : 'false'}
    >
      {layout.parts.map((part, i) => {
        const key = `${part.kind}-${String(i)}`;
        switch (part.kind) {
          case 'label':
            return (
              <div
                key={key}
                className={`ppText tone-${part.tone}`}
                style={{ '--x': part.x, '--y': part.y, '--fs': part.fs } as CSSProperties}
                data-lit={part.id !== undefined && lights.lit.has(part.id) ? 'true' : undefined}
                aria-hidden="true"
              >
                {part.text}
              </div>
            );
          case 'key':
            return (
              <Key
                key={key}
                part={part}
                asleep={asleep}
                lit={lights.lit.has(part.id)}
                onKey={onKey}
                onKeyUp={onKeyUp}
                onKeyHold={onKeyHold}
              />
            );
          case 'bar':
            return (
              <div
                key={key}
                className="pp ppBar"
                style={at(part.rect, { '--fs': part.fs })}
                aria-hidden="true"
              >
                {part.text}
              </div>
            );
          case 'knob':
            return <Knob key={key} part={part} asleep={asleep} />;
          case 'fader':
            return <Fader key={key} part={part} asleep={asleep} />;
          case 'platter':
            return (
              <Platter
                key={key}
                asleep={asleep}
                part={part}
                size={lights.decks[part.side === 'B' ? 'B' : 'A'].record}
              />
            );
          case 'pad':
            return (
              <Pad
                key={key}
                part={part}
                asleep={asleep}
                lit={lights.lit.has(part.id)}
                onKey={onKey}
                onKeyUp={onKeyUp}
                onKeyHold={onKeyHold}
              />
            );
          case 'oled':
            return (
              <Oled
                key={key}
                part={part}
                cells={lights.decks[part.side === 'B' ? 'B' : 'A'].cells}
              />
            );
          case 'box':
            return <div key={key} className={`pp ppBox tone-${part.tone}`} style={at(part.rect)} />;
          case 'panel':
            return <div key={key} className="pp ppPanel" style={at(part.rect)} />;
          case 'tick':
            return <div key={key} className="pp ppTick" style={at(part.rect)} />;
          case 'paddle':
            return <Paddle key={key} part={part} asleep={asleep} />;
          case 'screen':
            return (
              <div key={key} className="pp ppScreen" style={at(part.rect)}>
                {screen}
              </div>
            );
          case 'meter':
            return <Meter key={key} part={part} level={level} />;
        }
      })}
    </div>
  );
}
